import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { learningAnalyticsAPI, StaffingData } from '../../services/learningAnalyticsApi';

const STATUS_COLORS: Record<string, string> = {
  'Critical': '#d32f2f',
  'Understaffed': '#f57c00',
  'Adequate': '#1976d2',
  'Well Staffed': '#388e3c',
};

const STATUS_CHIP_COLORS: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
  'Critical': 'error',
  'Understaffed': 'warning',
  'Adequate': 'info',
  'Well Staffed': 'success',
};

const StaffingOptimizationTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StaffingData | null>(null);
  const [year, setYear] = useState<string>('');
  const [district, setDistrict] = useState<string>('');
  const [level, setLevel] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (year) params.year = parseInt(year);
      if (district) params.district = district;
      if (level) params.level = parseInt(level);

      const response = await learningAnalyticsAPI.getStaffingAnalytics(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load staffing analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, district, level]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const { summary, str_distribution, qualifications, experience, schools } = data;

  const districts = schools
    ? Array.from(new Set(schools.map(s => s.district_name))).sort()
    : [];

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Year" value={year}
              onChange={(e) => setYear(e.target.value)}>
              <MenuItem value="">Current</MenuItem>
              {[2026, 2025, 2024, 2023, 2022, 2021].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)}>
              <MenuItem value="">All Districts</MenuItem>
              {districts.map(d => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="School Level" value={level}
              onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="3">Pre-Primary</MenuItem>
              <MenuItem value="1">Primary</MenuItem>
              <MenuItem value="2">Secondary</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Avg Student-Teacher Ratio
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {summary.avg_student_teacher_ratio.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Understaffed Schools
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#f57c00' }}>
                {summary.understaffed_count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Critical Gaps
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#d32f2f' }}>
                {summary.critical_count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Well Staffed
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#388e3c' }}>
                {summary.well_staffed_count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* STR Distribution Bar Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>STR Distribution</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Number of schools in each student-teacher ratio range.
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={str_distribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#1976d2" name="Schools" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Qualifications Pie Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Teacher Qualifications</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Distribution of teacher qualifications.
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={qualifications}
                    dataKey="count"
                    nameKey="qualification"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label={({ qualification, count }: { qualification: string; count: number }) =>
                      `${qualification}: ${count}`
                    }
                    labelLine
                  >
                    {qualifications.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number, name: string) => [value.toLocaleString(), name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Experience Distribution Bar Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Experience Distribution</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Teachers grouped by years of experience.
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={experience}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#7b1fa2" name="Teachers" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Schools Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>School Staffing Details</Typography>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>School</TableCell>
                  <TableCell>District</TableCell>
                  <TableCell align="right">Teachers</TableCell>
                  <TableCell align="right">Enrollment</TableCell>
                  <TableCell align="right">STR</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schools.map((s) => (
                  <TableRow key={s.school_reg_number} hover>
                    <TableCell>{s.school_name}</TableCell>
                    <TableCell>{s.district_name}</TableCell>
                    <TableCell align="right">{s.teacher_count}</TableCell>
                    <TableCell align="right">{s.enrollment.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {s.student_teacher_ratio.toFixed(1)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={s.status}
                        size="small"
                        color={STATUS_CHIP_COLORS[s.status] || 'default'}
                        sx={
                          !STATUS_CHIP_COLORS[s.status]
                            ? { backgroundColor: STATUS_COLORS[s.status] || '#999', color: '#fff' }
                            : undefined
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StaffingOptimizationTab;
